import {LocalStorageUtils} from "../../../infrastructure/utils/local-storage.utils";
import {BaseAnalyticsProvider} from "../base-analytics-provider";
import {KinesisEventModel} from "./kinesis-event-model";
import {AwsConfig} from "./aws-config";
import {BaseAnalyticsEvent} from "../../../infrastructure/models/analytics/base-analytics-event";
import {KinesisProviderApi} from "./kinesis-provider.api";
import {AppStore} from "../../../core/app/app-store";
import {LoggerUtils} from "../../../infrastructure/utils/logger/logger-utils";
import {HistoryManager} from "../../../core/managers/history-manager";
import {PageViewEvent} from "../../../infrastructure/models/analytics/page-view-event";
import {AnalyticsUserDataEventModel} from "../analytics-user-data-event-model";
import {KinesisUserDataEventModel} from "./kinesis-user-data-event-model";
import {APP} from "../../../core/app/consts";
import {DeviceUtils} from "../../../infrastructure/utils/device-utils";
import {ResolutionUtils} from "../../../infrastructure/utils/resolution-utils";
import {LOCAL_STORAGE_KEYS, SESSION_STORAGE_KEYS} from "../../../core/consts";
import {SessionStorageUtils} from "../../../infrastructure/utils/session-storage.utils";
import {DatetimeUtils} from "../../../infrastructure/utils/datetime-utils";
import {singleton} from "../../../infrastructure/decorators/singleton";
import {observable, makeObservable, autorun, computed} from 'mobx';
import {KinesisRetryUtils} from "./kinesis-retry-utils";
import {CoralogixRum} from '@coralogix/browser';
import {CoralogixLogSeverity} from "@coralogix/browser/src/types-external";

@singleton
export class KinesisProvider extends BaseAnalyticsProvider {

  static injectables = [...BaseAnalyticsProvider.injectables, DeviceUtils, ResolutionUtils, LocalStorageUtils, DatetimeUtils, SessionStorageUtils];

  name: String = APP.ANALYTICS.PROVIDERS.kinesis;
  api: KinesisProviderApi = null;
  partitionKey = null;
  initPromise = null;
  visitId = null;
  ipAddress = null;
  langId = null;
  history = [];
  userDataHistory = [];
  results = [];
  errors = [];
  @observable events = [];
  onEventCreated = null;
  config: AwsConfig;
  initialized: Boolean = false;
  batch = [];
  sequence: Number;
  streamEvent: AnalyticsUserDataEventModel = null;
  historyManager: HistoryManager;
  datetimeUtils: DatetimeUtils;

  constructor(appStore: AppStore, logger: LoggerUtils, historyManager: HistoryManager, deviceUtils: DeviceUtils, resolutionUtils: ResolutionUtils, localStorageUtils: LocalStorageUtils, datetimeUtils: DatetimeUtils, sessionStorageUtils: SessionStorageUtils) {
    super(appStore, logger, historyManager);

    this.datetimeUtils = datetimeUtils;
    this.deviceUtils = deviceUtils;
    this.resolutionUtils = resolutionUtils;
    this.localStorageUtils = localStorageUtils;
    this.sessionStorageUtils = sessionStorageUtils;
    this.datetimeUtils = datetimeUtils;
    this.sequence = 0;
    this.visitId = this.getVisitId();
    this.partitionKey = localStorageUtils.get(LOCAL_STORAGE_KEYS.kinesisPartitionKey);

    if (!this.partitionKey) {
      this.partitionKey = Math.random().toString(36);

      localStorageUtils.set(LOCAL_STORAGE_KEYS.kinesisPartitionKey, this.partitionKey);
    }

    this.events = [];

    makeObservable(this);

    autorun(() => {
      if (this.api) this.api.shouldSupportConsent = this.appState.shouldSupportConsent;
    });

    this.createEvent = this.createEvent.bind(this);
  }

  get batchEventsCount() {
    return this.batch.length;
  }

  getVisitId(): String {
    let visitId: String = this.sessionStorageUtils.get(SESSION_STORAGE_KEYS.visitId);

    if (!visitId) {
      visitId = Math.random().toString(36);
      this.sessionStorageUtils.set(SESSION_STORAGE_KEYS.visitId, visitId);
    }

    return visitId;
  }

  async sendPageViewEvent(url: String) {
    const pageViewEvent: PageViewEvent = new PageViewEvent(url);

    this.createEvent(pageViewEvent)
  }

  init(props = {}) {
    super.init();

    const langId = 1;
    const ipAddress = null;
    // const config = new AwsConfig(AWS);

    this.langId = langId;
    this.ipAddress = ipAddress;
    // this.config = config;
    this.initialized = true;
    this.api = new KinesisProviderApi({
      partitionKey: this.partitionKey,
      shouldSupportConsent: this.appState.shouldSupportConsent
    });


    if (this.streamEvent) {
      this.sendUserData(this.streamEvent);
    }
  }

  async sendEvents(events) {
    let eventsToSend = events
    if (!this.initialized) {
      return null;
    }

    if (this.api && !this.api.isVendorConsentGranted) {
      eventsToSend = this.sanitizedEvents
    }

    await this.initPromise;
    await this.api.send(eventsToSend);

    this.history.push(...eventsToSend);

    return eventsToSend;
  }

  @computed get sanitizedEvents() {
    return this.events?.map(event => {
      event.visitor_id = "missing_consent";
      event.ip = "missing_consent";

      if (event.properties && typeof event.properties === "object") {
        event.properties.device_id = "missing_consent";
      }

      return event;
    });
  }

  sendUserData(eventData: AnalyticsUserDataEventModel) {
    if (!this.initialized) {
      this.streamEvent = eventData;
      return null;
    }

    const kinesisEventData = new KinesisUserDataEventModel(eventData, this.partitionKey);


    if (this.api && !this.api.isVendorConsentGranted) {
      kinesisEventData.visitor_id = "missing_consent";
      kinesisEventData.ip = "missing_consent";
      kinesisEventData.app_device_id = "missing_consent";
    }

    kinesisEventData.publisher_id = this.appState.settings.publisherSettings.id;
    kinesisEventData.web_type = this.appState.settings.isWidget ? APP.ANALYTICS.WEB_TYPES.widget : APP.ANALYTICS.WEB_TYPES.site;
    kinesisEventData.widget_type = APP.ANALYTICS.WIDGET_TYPES[this.appState.settings.widgetType];

    this.userDataHistory.push(kinesisEventData);

    this.api.updateUserData(kinesisEventData);
  }


  createEvent(event: BaseAnalyticsEvent, disableSend = false) {
    const eventData = new KinesisEventModel(event, ++this.sequence, this.deviceUtils, this.resolutionUtils, this.datetimeUtils);
    const KINESIS_MAXIMUM_CHARS = 200;

    eventData.is_user_driven = event.isUserDriven;
    eventData.visitor_id = this.partitionKey;
    eventData.visit_id = this.visitId;
    eventData.ip = this.ipAddress;
    eventData.lang_id = this.appState.locale?.language.id;
    eventData.publisher_id = this.appState.settings.publisherSettings.id;
    eventData.isSync = event.isSync;
    eventData.country_id = this.appState.locale?.userCountryId;
    eventData.web_type = this.appState.settings.isWidget ? APP.ANALYTICS.WEB_TYPES.widget : APP.ANALYTICS.WEB_TYPES.site;
    eventData.widget_type = APP.ANALYTICS.WIDGET_TYPES[this.appState.settings.widgetType];
    eventData.url = window.location.href.slice(0, KINESIS_MAXIMUM_CHARS);
    eventData.show_wizard_ab_test = !this.appState.wizardDisplayModel.blockDisplay;
    eventData.isLiveStreamingLightMode = this.appState.appLiveStreamingDisplayModel.isLightMode;
    eventData.website_notifications = this.appState.appNotificationDisplayModel.websiteNotifications;

    if (!disableSend && event.isPopulated) {
      if (this.onEventCreated) {
        this.onEventCreated(eventData);
      }

      this.events.push(eventData);
      this.batch.push(eventData);
    }

    return eventData;
  }

  async send(events = this.batch) {
    const sendEvents = [...events];

    if (sendEvents.length) {
      const sentEvents = await this.sendEvents(sendEvents);

      const retryUtils = new KinesisRetryUtils();
      const maxRetries = retryUtils.getMaxRetries();

      // Remove events that succeeded, exceeded max retries, or have non-retryable errors
      this.batch = this.batch.filter(event => {
        // Keep if event succeeded
        if (event.sent) {
          return false;
        }

        // Keep if event never attempted (retryCount === 0)
        const retryCount = event.retryCount || 0;
        if (retryCount === 0) {
          return true;
        }

        // Drop if exceeded max retries
        if (retryCount >= maxRetries) {
          CoralogixRum.log(
            CoralogixLogSeverity.Warn,
            `Dropping event after ${retryCount} failed attempts`,
            {
              eventName: event.event_name,
              lastErrorCode: event.lastErrorCode,
              retryCount: event.retryCount,
              lastRetryTimestamp: event.lastRetryTimestamp
            }
          );
          return false;
        }

        // Drop if error is non-retryable
        if (event.lastErrorCode && !retryUtils.isRetryableError(event.lastErrorCode)) {
          CoralogixRum.log(
            CoralogixLogSeverity.Warn,
            `Dropping event with non-retryable error: ${event.lastErrorCode}`,
            {
              eventName: event.event_name,
              lastErrorCode: event.lastErrorCode,
              retryCount: event.retryCount,
              lastRetryTimestamp: event.lastRetryTimestamp
            }
          );
          return false;
        }

        // Keep event - it has retries remaining and error is retryable
        return true;
      });

      return sentEvents;
    }

    return null;
  }

  async flush() {
    return await this.send();
  }
}
